import React, { Fragment } from 'react';
import Parser from 'html-react-parser';
import HelperFunctions from '../../../global/HelperFunctions';
import LoadingSpinner from '../../../global/LoadingSpinner';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useGetSections } from 'pages/documents_v2/hooks/useGetSections';
import { useGetRenderedProductsQuery } from 'features/reimbursements/reimbursements';

export function RenderedReimbursementProductsAsFunctionInner({
    blockId,
    blockData,
    document,
    markChanges = true,
    activeVariantId,
}) {
    const renderedProductsQuery = useGetRenderedProductsQuery(getQueryParams());
    const reimbursementProducts = renderedProductsQuery.data?.reimbursement?.renderedReimbursementProducts ?? {};

    if (renderedProductsQuery.isLoading || renderedProductsQuery.isUninitialized) {
        return <LoadingSpinner inline size="sm" />;
    }

    return <RenderedReimbursementProducts reimbursementProducts={reimbursementProducts} document={document} />;

    function getQueryParams() {
        let activeRevisionStart = undefined;

        if (markChanges && document.activeRevisionId) {
            const activeRevision = document.revisions.find((revision) => revision.id === document.activeRevisionId);
            activeRevisionStart = activeRevision?.dateStart;
        }

        const formData = new FormData();
        formData.append('content[blockId]', blockId);
        formData.append('content[content][code]', blockData.code);
        formData.append('content[variantId]', activeVariantId);
        formData.append('content[categoryId]', document.reimbursementCategoryId);
        formData.append('content[locale]', 'nl');
        formData.append('content[showChanges]', true);
        formData.append('content[showChangesInPreview]', true);
        formData.append(
            'content[overrideNoReimbursementText]',
            Boolean(document.properties?.overrideNoReimbursementText),
        );

        if (activeRevisionStart) {
            formData.append('content[changesFromTimestamp]', activeRevisionStart);
        }

        const searchParams = new URLSearchParams(formData);

        return searchParams.toString();
    }
}

export function RenderedReimbursementProducts({ reimbursementProducts = null, document = null }) {
    const { t } = useTranslation('reimbursements');

    if (_.isEmpty(reimbursementProducts)) {
        return <div>{t('reimbursement.product.noProductLinked')}</div>;
    }

    const {
        name = '',
        secondaryName = '',
        products = [],
        secondaryProducts = [],
        showAsSeparate = false,
    } = reimbursementProducts;

    return (
        <div className="uk-text-left">
            <ProductsTable name={name} products={products} document={document} />

            {showAsSeparate && (
                <div className="pt-3">
                    <ProductsTable name={secondaryName ?? name} products={secondaryProducts} document={document} />
                </div>
            )}
        </div>
    );
}

export function ProductsTable({ name = '', products = [], document = null }) {
    const { t } = useTranslation('reimbursements');
    const sections = useGetSections(document?.id, true);

    return (
        <>
            {name !== '' && <div className="font-weight-bold mb-2">{Parser(name)}</div>}

            <table className="uk-table uk-table-small uk-table-striped mt-0">
                <thead>
                    <tr>
                        <th />
                        <th>{t('proposition.name')}</th>
                        <th>{t('proposition.editProposition.properties.description')}</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map((_product) => {
                        const {
                            code = '',
                            name = '',
                            identical = false,
                            identicalName = '',
                            identicalCode = '',
                        } = _product;

                        let description = translateTags(_product.description);
                        if (sections.length > 0) {
                            description = HelperFunctions.prepareRefs(description, sections);
                        }

                        return (
                            <tr
                                key={`tableReimbursementProduct${_product.reimbursementProductId}-${_product.productId}-${_product.variantPackageProductId}`}
                            >
                                <td>
                                    <span className="font-weight-bold pr-2">{code}</span>
                                </td>
                                <td width="50%">
                                    {name}

                                    {identical && (
                                        <div
                                            style={{
                                                fontSize: 9,
                                            }}
                                        >
                                            {t('reimbursement.product.misc.identicalShort', {
                                                identicalCode: identicalCode,
                                                identicalName: identicalName,
                                            })}
                                        </div>
                                    )}
                                </td>
                                <td width="50%">{Parser(description)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </>
    );

    function translateTags(text) {
        return text.replace(/\[trans (.*?)]/gm, (match) => {
            const tagString = match.slice(7, -1);
            const params = {};

            tagString
                .split(' ')
                .map((_param) => _param.split('='))
                .forEach((_param) => {
                    params[_param[0]] = _param[1];
                });

            if (params.hasOwnProperty('key')) {
                return t(`reimbursements:tags.${params.key}`);
            }

            return text;
        });
    }
}
